/// <reference path="../../../../../../calculator.d.ts" />

import { CommonModule, DecimalPipe } from '@angular/common';
import { AfterViewInit, ChangeDetectorRef, Component, Input } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Store, createSelector } from '@ngrx/store';

import { FreyaCommonModule } from '../../../../freya-common/freya-common.module';
import { quickJsActions } from '../../../../quickjs/quickjs.actions';
import { CALCULATOR_DELIMITER, selectUserInputs, selectCalculatedState } from '../../../../quickjs/quickjs.feature';
import { selectCanEditSharedCrewPay } from '../../../../shared/unsaved-changes-banner/unsaved-changes-banner.selectors';
import { brandingFeature } from '../../../../state/branding.store';
import { selectJob } from '../../../job-tool.reducer';
import { CalculatorDatePickerComponent } from '../calculator-date-picker/calculator-date-picker.component';
import { CrewPayTableComponent } from '../crew-pay-table/crew-pay-table.component';
import { FpRevenueSplitTableComponent } from '../fp-revenue-split-table/fp-revenue-split-table.component';

@Component({
  selector: 'app-calculator-field',
  standalone: true,
  imports: [
    CommonModule,
    FreyaCommonModule,
    FormsModule,
    CalculatorFieldComponent,
    CalculatorDatePickerComponent,
    CrewPayTableComponent,
    FpRevenueSplitTableComponent,
    DecimalPipe,
  ],
  templateUrl: './mr-calculator-fields.component.html',
  styleUrl: './mr-calculator-fields.component.scss'
})
export class CalculatorFieldComponent implements AfterViewInit {

  @Input() formElement: FormElement;
  @Input() elementIndex?: string;
  @Input() plain = false;

  public calculatorActions = quickJsActions;

  public delimiter = CALCULATOR_DELIMITER;

  viewModel$ = this.store.select(createSelector(
    selectUserInputs,
    selectCalculatedState,
    selectJob,
    brandingFeature.selectCurrency,
    selectCanEditSharedCrewPay,
    (userInputs, calculatedState, job, zoneCurrency, canEditSharedCrewPay) => {

      let value: number | string | boolean;
      let placeholder: string;

      const calculatedValue = this._getCalculatedValue(calculatedState);
      const key = this.formElement.key;

      const userInput = userInputs[key];
      const showResetButton = !(userInputs[key] === undefined || userInputs[key] === null);

      const elementsToUseCalculatedValue = [
        'select_dropdown',
        'slider',
        'checkbox',
      ];
      if (this.isInput && elementsToUseCalculatedValue.includes(this.formElement.elementType)) {
        value = (userInput === undefined || userInput === null ? calculatedValue : userInput);
      } else if (this.isInput) {
        value = userInput;
      }

      if (this.isInput) {
        placeholder = calculatedValue;
      } else {
        value = calculatedValue;
      }

      if (this.formElement.elementType === 'output_number' || this.formElement.elementType === 'output_percent') {
        value = this._coerceNumber(value);
      }

      let className = '';
      if (this.formElement.elementType === 'select_dropdown' && this.formElement.optionClassNames && value !== undefined) {
        className = this.formElement.optionClassNames[value as string] || '';
      }

      // Hide output_text fields if they have no value
      const hidden = this.formElement.elementType === 'output_text' && !value;

      // The crew-pay table lifts itself above the shared-job overlay, but it is only reachable
      // once its collapsed section is opened, so that section's header has to be lifted too.
      const aboveSharedJobOverlay = canEditSharedCrewPay
        && this.formElement.elementType === 'section'
        && this.formElement.elements.some((e) => e.elementType === 'crew_pay_table');

      return {
        hidden,
        aboveSharedJobOverlay,
        value,
        placeholder,
        showResetButton,
        currency: job?.currency || zoneCurrency,
        className,
        ...this._getSliderRange({ calculatedState }),
      }
    }
  ));

  constructor(
    public store: Store,
    private cd: ChangeDetectorRef,
  ) {}

  private _coerceNumber(value: unknown) {
    if (typeof value === 'number') return value;
    if (typeof value === 'string' && value.trim() !== '' && !isNaN(+value)) return +value;
    return null;
  }

  private _getCalculatedValue(calculatedState: { [ key: string ]: ElementValue }) {

    const subKeys = this.formElement.key?.split(this.delimiter);

    if (subKeys?.length > 1) {
      return this._getCalculatedCellValue({ calculatedState });
    }

    if (this.formElement.key in calculatedState) {
      return calculatedState[this.formElement.key];
    }

    if ('value' in this.formElement) {
      return this.formElement.value;
    }
  }

  private _getCalculatedCellValue({ calculatedState }: {
    calculatedState: { [ key: string ]: ElementValue };
  }) {

    const [ tableKey, _rowKey, colKey ] = this.formElement.key.split(this.delimiter);

    const table = calculatedState[tableKey];

    if (!table) return;

    const row: { key: string, [prop: string]: any } = table[this.elementIndex];

    if (!row) { return; }

    return row[colKey];
  }

  private _getSliderRange({ calculatedState }: {
    calculatedState: { [ key: string ]: ElementValue };
  }) {

    if (this.formElement.elementType !== 'slider') {
      return {};
    }

    return {
      min: calculatedState[this.formElement.minKey],
      max: calculatedState[this.formElement.maxKey],
    }
  }

  public trackByKey(index: number, item: any): string {
    return item.key || index;
  }

  public get insideGrid() {
    return [
      'input_text',
      'input_number',
      'input_currency',
      'select_dropdown',
      'output_text',
      'output_number',
      'output_percent',
      'output_currency',
      'slider',
      'checkbox',
    ].includes(this.formElement.elementType);
  }

  public get isInput() {
    return [
      'input_text',
      'input_number',
      'input_currency',
      'select_dropdown',
      'date_picker',
      'button',
      'slider',
      'checkbox',
    ].includes(this.formElement.elementType);
  }

  ngAfterViewInit(): void {
    // Prevent changed after checked error caused by formatting placeholders
    if (this.formElement.elementType === 'input_number' && this.formElement.suffix) {
      this.cd.detectChanges();
    }
  }
}
